import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Settings, LogOut, User, Calendar, Clock, Loader2, Eye, EyeOff, CheckCircle, AlertCircle, Lock } from 'lucide-react';
import { Header } from '../components/shared/Header';
import { Footer } from '../components/landing/Footer';
import { Button } from '../components/shared/Button';
import { Modal } from '../components/shared/Modal';
import { Input } from '../components/shared/Input';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services';
import { resultsService, type PerformanceSummary } from '../services/results.service';
import { getPlanLabel } from '../utils/planLabels';
import { isPassesModel } from '../config/pricing';

type NotificationKey = 'examReminders' | 'progressUpdates' | 'newsletter';

interface EditFieldErrors {
  name?: string;
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

type TabType = 'overview' | 'settings' | 'subscription';

export const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading: authLoading, logout, refreshUser } = useAuth();
  const [stats, setStats] = useState<PerformanceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  // Edit profile modal state
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editSuccess, setEditSuccess] = useState<string | null>(null);
  const [editFieldErrors, setEditFieldErrors] = useState<EditFieldErrors>({});

  // Preferences state
  const [prefSaving, setPrefSaving] = useState<NotificationKey | null>(null);
  const [prefError, setPrefError] = useState<string | null>(null);
  const [prefSuccess, setPrefSuccess] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const summary = await resultsService.getPerformanceSummary();
        setStats(summary);
      } catch (error) {
        console.error('Failed to load stats:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchStats();
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [user, authLoading]);

  const handleLogout = async () => {
    await logout();
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
            <p className="text-gray-600">{t('common.loading')}</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center text-error">
            <p>{t('common.error')}</p>
            <Button variant="primary" className="mt-4" onClick={() => navigate('/dashboard')}>
              {t('profile.backToDashboard')}
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const getApiErrorMessage = (err: unknown, fallback: string): string => {
    const res = err as { response?: { data?: { message?: string | string[] } } };
    const msg = res.response?.data?.message;
    return Array.isArray(msg) ? msg.join(' ') : (msg as string) || fallback;
  };

  const openEditModal = () => {
    setEditName(user.name);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowPasswords(false);
    setEditFieldErrors({});
    setEditError(null);
    setEditSuccess(null);
    setIsEditOpen(true);
  };

  const closeEditModal = () => {
    if (isSaving) return;
    setIsEditOpen(false);
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setEditError(null);
    setEditSuccess(null);

    const fieldErrors: EditFieldErrors = {};
    const trimmedName = editName.trim();
    if (!trimmedName) {
      fieldErrors.name = t('validation.nameRequired');
    } else if (trimmedName.length < 2) {
      fieldErrors.name = t('validation.nameMin');
    } else if (trimmedName.length > 50) {
      fieldErrors.name = t('validation.maxLength', { max: 50 });
    }

    const wantsPasswordChange = Boolean(currentPassword || newPassword || confirmPassword);
    if (wantsPasswordChange) {
      if (!currentPassword) {
        fieldErrors.currentPassword = t('profile.edit.currentPasswordRequired');
      }
      if (!newPassword) {
        fieldErrors.newPassword = t('validation.password');
      }
      if (!confirmPassword) {
        fieldErrors.confirmPassword = t('validation.passwordConfirm');
      } else if (newPassword && newPassword !== confirmPassword) {
        fieldErrors.confirmPassword = t('validation.passwordMatch');
      }
    }

    setEditFieldErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) {
      return;
    }

    setIsSaving(true);
    try {
      const nameChanged = trimmedName !== user.name;
      let passwordChanged = false;

      if (nameChanged) {
        await userService.updateProfile({ name: trimmedName });
      }
      if (wantsPasswordChange) {
        await userService.changePassword({ currentPassword, newPassword });
        passwordChanged = true;
      }

      if (nameChanged) {
        await refreshUser();
        setEditName(trimmedName);
      }

      if (passwordChanged) {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setEditSuccess(t('profile.edit.passwordSuccess'));
      } else if (nameChanged) {
        setEditSuccess(t('profile.edit.nameSuccess'));
      } else {
        setEditSuccess(t('profile.edit.noChanges'));
      }
    } catch (err) {
      setEditError(getApiErrorMessage(err, t('common.error')));
    } finally {
      setIsSaving(false);
    }
  };

  const handleNotificationChange = async (key: NotificationKey, value: boolean) => {
    setPrefError(null);
    setPrefSuccess(null);
    setPrefSaving(key);
    try {
      await userService.updatePreferences({ notifications: { [key]: value } });
      await refreshUser();
      setPrefSuccess(t('profile.preferencesSaved'));
    } catch (err) {
      setPrefError(getApiErrorMessage(err, t('profile.preferencesError')));
    } finally {
      setPrefSaving(null);
    }
  };

  const totalStudyHours = stats ? Math.round((stats.totalStudyTime || 0) / 60 * 10) / 10 : 0;

  const planId = user.subscription?.plan;
  const planLabel = getPlanLabel(planId, t);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      {/* Spacer for fixed header */}
      <div className="h-20" />

      <main className="flex-1 container mx-auto px-4 py-8 max-w-2xl">
        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-lg mb-8">
          {/* Header Background */}
          <div className="h-32 bg-gradient-to-r from-primary to-secondary relative rounded-t-2xl">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIwOS0xLjc5MS00LTQtNHMtNCAxLjc5MS00IDQgMS43OTEgNCA0IDQgNC0xLjc5MSA0LTR6bTAtMThjMC0yLjIwOS0xLjc5MS00LTQtNHMtNCAxLjc5MS00IDQgMS43OTEgNCA0IDQgNC0xLjc5MSA0LTR6bTE4IDBjMC0yLjIwOS0xLjc5MS00LTQtNHMtNCAxLjc5MS00IDQgMS43OTEgNCA0IDQgNC0xLjc5MSA0LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30 rounded-t-2xl" />
          </div>
          
          {/* Profile Content */}
          <div className="px-6 pb-6 -mt-16 relative">
            {/* Avatar & Basic Info */}
            <div className="flex flex-col sm:flex-row items-center sm:items-end gap-4 mb-6">
              <div className="w-32 h-32 bg-gradient-to-br from-primary to-secondary rounded-full border-4 border-white shadow-xl flex items-center justify-center text-5xl font-bold text-white flex-shrink-0">
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div className="text-center sm:text-left pb-2">
                <h1 className="text-2xl font-bold text-gray-900">{user.name}</h1>
                <p className="text-gray-600 flex items-center justify-center sm:justify-start mt-1">
                  <Mail className="w-4 h-4 mr-2" />
                  {user.email}
                </p>
              </div>
            </div>

            {/* Exam Level & Program */}
            <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded-xl">
              <div className="text-center sm:text-left">
                <p className="text-sm text-gray-500 font-medium">{t('profile.examProgram')}</p>
                <p className="text-xl font-bold text-primary">{user.preferences?.targetLevel || 'B1'}</p>
              </div>
              <div className="text-center sm:text-left">
                <p className="text-sm text-gray-500 font-medium">{t('profile.currentLevel')}</p>
                <p className="text-xl font-bold text-secondary">{planLabel}</p>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-primary/5 p-4 rounded-xl text-center">
                <p className="text-2xl font-bold text-primary">{stats?.totalAttempts ?? 0}</p>
                <p className="text-xs text-gray-600">{t('profile.stats.examsTaken')}</p>
              </div>
              <div className="bg-secondary/10 p-4 rounded-xl text-center">
                <p className="text-2xl font-bold text-secondary">{stats?.averageScore ?? 0}%</p>
                <p className="text-xs text-gray-600">{t('profile.stats.avgScore')}</p>
              </div>
              <div className="bg-success/10 p-4 rounded-xl text-center">
                <p className="text-2xl font-bold text-success">{stats?.passRate ?? 0}%</p>
                <p className="text-xs text-gray-600">{t('profile.stats.dayStreak')}</p>
              </div>
              <div className="bg-purple-100 p-4 rounded-xl text-center">
                <p className="text-lg font-bold text-purple-600">{stats?.passedExams ?? 0}</p>
                <p className="text-xs text-gray-600">{t('profile.stats.bestSection')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl">
          {(['overview', 'settings', 'subscription'] as TabType[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 px-4 py-3 font-medium text-sm rounded-lg transition-all ${
                activeTab === tab
                  ? 'bg-white text-primary shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t(`profile.tabs.${tab}`)}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          {activeTab === 'overview' && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                {t('profile.accountOverview')}
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                  <span className="text-gray-700">{t('profile.userId')}</span>
                  <span className="font-mono text-sm text-gray-500 bg-gray-200 px-2 py-1 rounded">{user.id}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-700">{t('profile.memberSince')}</span>
                  </div>
                  <span className="text-gray-600">
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString('nl-NL') : t('profile.memberSinceDate')}
                  </span>
                </div>
                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-700">{t('profile.totalStudyHours')}</span>
                  </div>
                  <span className="text-gray-600">{totalStudyHours} {t('common.hours')}</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5 text-primary" />
                {t('profile.preferences')}
              </h3>
              <div className="space-y-3">
                {prefSuccess && (
                  <div className="flex items-center gap-2 rounded-xl border border-success/25 bg-success/10 px-4 py-3 text-sm text-success" role="alert">
                    <CheckCircle className="w-4 h-4 flex-shrink-0" />
                    {prefSuccess}
                  </div>
                )}
                {prefError && (
                  <div className="flex items-center gap-2 rounded-xl border border-error/25 bg-error/10 px-4 py-3 text-sm text-error" role="alert">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {prefError}
                  </div>
                )}
                <label className="flex items-center p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                    checked={user.preferences?.notifications?.examReminders ?? true}
                    onChange={(e) => handleNotificationChange('examReminders', e.target.checked)}
                    disabled={prefSaving !== null}
                  />
                  <span className="ml-3 text-gray-700">{t('profile.emailNotifications')}</span>
                </label>
                <label className="flex items-center p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                    checked={user.preferences?.notifications?.progressUpdates ?? true}
                    onChange={(e) => handleNotificationChange('progressUpdates', e.target.checked)}
                    disabled={prefSaving !== null}
                  />
                  <span className="ml-3 text-gray-700">{t('profile.weeklyProgress')}</span>
                </label>
                <label className="flex items-center p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                    checked={user.preferences?.notifications?.newsletter ?? false}
                    onChange={(e) => handleNotificationChange('newsletter', e.target.checked)}
                    disabled={prefSaving !== null}
                  />
                  <span className="ml-3 text-gray-700">{t('profile.marketingEmails')}</span>
                </label>
              </div>
            </div>
          )}

          {activeTab === 'subscription' && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">{t('profile.subscription')}</h3>
              <div className="bg-primary/5 border border-primary/20 p-5 rounded-xl mb-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-gray-600">{t('profile.activePlan')}</span>
                  <span className="px-3 py-1 bg-success/10 text-success text-xs font-medium rounded-full">
                    {user.subscription?.status || 'free'}
                  </span>
                </div>
                <p className="text-xl font-bold text-primary mb-1">{planLabel}</p>
                {user.subscription?.currentPeriodEnd && (
                  <div className="mt-4 pt-4 border-t border-primary/10">
                    <p className="text-sm text-gray-600">
                      <strong>{t('profile.renewsOn')}:</strong>{' '}
                      {new Date(user.subscription.currentPeriodEnd).toLocaleDateString('nl-NL', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </p>
                  </div>
                )}
              </div>
              <Button 
                variant="secondary" 
                className="w-full"
                onClick={() => navigate(isPassesModel ? '/pricing' : '/dashboard/subscription')}
              >
                {t(isPassesModel ? 'profile.viewPasses' : 'profile.manageSubscription')}
              </Button>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-4">
          <Button
            variant="tertiary"
            className="w-full"
            icon={<Settings className="w-4 h-4" />}
            onClick={openEditModal}
          >
            {t('profile.editProfile')}
          </Button>
          <Button 
            variant="tertiary" 
            className="w-full text-error hover:bg-error/10"
            icon={<LogOut className="w-4 h-4" />}
            onClick={handleLogout}
          >
            {t('profile.signOut')}
          </Button>
        </div>
      </main>

      <Footer />

      <Modal
        isOpen={isEditOpen}
        onClose={closeEditModal}
        title={t('profile.edit.title')}
        size="medium"
      >
        <form onSubmit={handleSaveProfile} className="space-y-5">
          {editSuccess && (
            <div className="flex items-center gap-2 rounded-xl border border-success/25 bg-success/10 px-4 py-3 text-sm text-success" role="alert">
              <CheckCircle className="w-4 h-4 flex-shrink-0" />
              {editSuccess}
            </div>
          )}
          {editError && (
            <div className="flex items-center gap-2 rounded-xl border border-error/25 bg-error/10 px-4 py-3 text-sm text-error" role="alert">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {editError}
            </div>
          )}

          <Input
            label={t('profile.edit.name')}
            value={editName}
            onChange={(e) => {
              setEditName(e.target.value);
              if (editFieldErrors.name) {
                setEditFieldErrors((prev) => ({ ...prev, name: undefined }));
              }
            }}
            error={editFieldErrors.name}
            maxLength={50}
            autoComplete="name"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('profile.edit.email')}
            </label>
            <input
              type="email"
              value={user.email}
              readOnly
              disabled
              className="w-full border border-gray-200 bg-gray-100 rounded-lg px-4 py-3 text-gray-500 cursor-not-allowed"
            />
            <p className="mt-1 text-sm text-gray-500">{t('profile.edit.emailReadOnly')}</p>
          </div>

          <div className="border-t border-gray-100 pt-5">
            <div className="flex items-center gap-2 mb-1">
              <Lock className="w-4 h-4 text-primary" />
              <h4 className="font-semibold text-gray-900">{t('profile.edit.changePassword')}</h4>
            </div>
            <p className="text-sm text-gray-500 mb-4">{t('profile.edit.passwordOptional')}</p>

            <div className="space-y-4">
              <Input
                label={t('profile.edit.currentPassword')}
                type={showPasswords ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => {
                  setCurrentPassword(e.target.value);
                  if (editFieldErrors.currentPassword) {
                    setEditFieldErrors((prev) => ({ ...prev, currentPassword: undefined }));
                  }
                }}
                error={editFieldErrors.currentPassword}
                autoComplete="current-password"
              />
              <Input
                label={t('profile.edit.newPassword')}
                type={showPasswords ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (editFieldErrors.newPassword) {
                    setEditFieldErrors((prev) => ({ ...prev, newPassword: undefined }));
                  }
                }}
                error={editFieldErrors.newPassword}
                autoComplete="new-password"
              />
              <Input
                label={t('profile.edit.confirmPassword')}
                type={showPasswords ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (editFieldErrors.confirmPassword) {
                    setEditFieldErrors((prev) => ({ ...prev, confirmPassword: undefined }));
                  }
                }}
                error={editFieldErrors.confirmPassword}
                autoComplete="new-password"
              />
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-gray-500">{t('profile.edit.passwordHint')}</p>
                <button
                  type="button"
                  onClick={() => setShowPasswords((prev) => !prev)}
                  className="inline-flex items-center gap-1 text-sm text-primary hover:underline whitespace-nowrap"
                >
                  {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  {showPasswords ? t('auth.hidePassword') : t('auth.showPassword')}
                </button>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="tertiary"
              className="flex-1"
              onClick={closeEditModal}
              disabled={isSaving}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              loading={isSaving}
              disabled={isSaving}
              icon={<CheckCircle className="w-4 h-4" />}
            >
              {t('common.save')}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ProfilePage;
